import React from 'react'
import PropTypes from 'prop-types'
import { makeT, sharedT } from '../../../../group_leader/helpers/i18n'
import { GENDER_VALUES } from '../../../helpers/players'

const t = makeT('group_leader.event_setup.players')

const genderLabel = (value) => t(`gender.${value}`)

// Module level so React sees one type (REACT-090); marks the names the endpoint requires.
const RequiredMark = () => <span className="gl-event-setup-ef-required" aria-hidden="true"> *</span>

const MemberFormFields = ({
  idPrefix,
  form,
  required = {},
  onChange,
  emailError,
  radioName,
  phoneFieldName,
  autoFocusFirst = false,
  showGhin = false,
  onCancel,
  onSave,
  saveLabel,
  saveDisabled = false,
}) => {
  // One labelled input of `width` grid columns; `after` renders beneath it, inside the group.
  const field = (key, idSuffix, width, label, inputProps = {}, after = null) => (
    <div className={`gl-event-setup-ef-group gl-event-setup-ef-group--${width}`}>
      <label className="gl-event-setup-ef-label" htmlFor={`${idPrefix}-${idSuffix}`}>{label}</label>
      <input
        className="gl-event-setup-ef-input"
        id={`${idPrefix}-${idSuffix}`}
        type="text"
        value={form[key]}
        onChange={(e) => onChange(key, e.target.value)}
        {...inputProps}
      />
      {after}
    </div>
  )
  const nameLabel = (key) => <>{t(`member_form.${key === 'firstName' ? 'first_name' : 'last_name'}`)}{required[key] && <RequiredMark/>}</>

  return (
    <>
      <div className="gl-event-setup-ef-row">
        {field('firstName', 'first', 2, nameLabel('firstName'), { 'aria-required': !!required.firstName, autoFocus: autoFocusFirst })}
        {field('lastName', 'last', 2, nameLabel('lastName'), { 'aria-required': !!required.lastName })}
        {field('email', 'email', 3, t('member_form.email'), {
          type: 'email',
          placeholder: t('member_form.email_placeholder'),
          // Marked invalid and described by the error, so a screen reader knows this field blocks the form.
          'aria-invalid': emailError ? true : undefined,
          'aria-describedby': emailError ? `${idPrefix}-email-error` : undefined,
        }, emailError && (
          <p className="gl-event-setup-field-error" id={`${idPrefix}-email-error`} role="alert">
            {t('member_form.email_error')}
          </p>
        ))}
      </div>

      <div className="gl-event-setup-ef-row">
        {field('handicapIndex', 'hi', 1, t('member_form.handicap'), {
          inputMode: 'decimal', placeholder: t('member_form.handicap_placeholder'),
        })}
        {showGhin && field('ghin', 'ghin', 2, t('member_form.ghin'), { inputMode: 'numeric', value: form.ghin || '' })}
        {phoneFieldName && field('phone', 'phone', 2, t('member_form.phone'), { type: 'tel' })}
        <div className="gl-event-setup-ef-group gl-event-setup-ef-group--2">
          <span className="gl-event-setup-ef-label">{t('member_form.gender')}</span>
          <div className="gl-event-setup-ef-radio-group" role="radiogroup" aria-label={t('member_form.gender')}>
            {GENDER_VALUES.map((value) => {
              // No gender on file reads as Unknown; nothing is sent until the leader picks one.
              const isChecked = (form.gender || 'unknown') === value
              return (
                // A label, so the native radio does the selecting and the keyboard needs no handlers.
                <label key={value} className="gl-event-setup-ef-radio-item">
                  <input
                    className="gl-event-setup-ef-radio-input"
                    type="radio"
                    name={radioName}
                    value={value}
                    checked={isChecked}
                    onChange={() => onChange('gender', value)}
                  />
                  <span className={`gl-event-setup-ef-radio-circle${isChecked ? ' is-checked' : ''}`} aria-hidden="true"/>
                  <span className={`gl-event-setup-ef-radio-text${isChecked ? ' is-checked' : ''}`}>{genderLabel(value)}</span>
                </label>
              )
            })}
          </div>
        </div>
      </div>
      <div className="gl-event-setup-ef-actions">
        <button className="gl-event-setup-ef-cancel-btn" type="button" onClick={onCancel}>{sharedT('cancel')}</button>
        <button className="gl-event-setup-ef-save-btn" type="button" onClick={onSave} disabled={saveDisabled}>{saveLabel}</button>
      </div>
    </>
  )
}

MemberFormFields.propTypes = {
  idPrefix: PropTypes.string.isRequired,
  // Which fields the panel's own endpoint refuses to do without.
  required: PropTypes.shape({ firstName: PropTypes.bool, lastName: PropTypes.bool }),
  form: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    handicapIndex: PropTypes.string,
    gender: PropTypes.string,
    phone: PropTypes.string,
    ghin: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  emailError: PropTypes.oneOfType([ PropTypes.bool, PropTypes.string ]),
  radioName: PropTypes.string.isRequired,
  phoneFieldName: PropTypes.string,
  autoFocusFirst: PropTypes.bool,
  showGhin: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saveLabel: PropTypes.node.isRequired,
  saveDisabled: PropTypes.bool,
}

export default MemberFormFields
